import { createRoute, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, VideoOff } from 'lucide-react'
import { projectRoute } from '../../route'
import { MeetFeatureGate } from '@/components/meet/feature-gate'
import { PreJoin, type PreJoinChoice } from '@/components/meet/pre-join'
import { WaitingScreen } from '@/components/meet/waiting-screen'
import { EndedScreen } from '@/components/meet/ended-screen'
import { VideoDock } from '@/components/meet/video-dock'
import { SharePreview } from '@/components/meet/share-preview'
import { ControlBar } from '@/components/meet/control-bar'
import { TranscriptionBanner } from '@/components/meet/transcription-banner'
import { EndMeetingDialog } from '@/components/meet/end-meeting-dialog'
import { RaisedHandsList } from '@/components/meet/raised-hands'
import { Button } from '@/components/ui/button'
import { orpc } from '@/lib/orpc-client'
import { useAuthState } from '@/lib/auth-state'
import { useIsMobile } from '@/hooks/use-mobile'
import { useDocumentClient } from '@/lib/documents/use-document-client'
import { useMeetingSession, type JoinInput } from '@/lib/meetings/meeting-session-context'
import { LivePanel } from '@/components/meet/live-panel'
import type { BlockEditorHandle } from '@/components/documents/block-editor'
import { closePreview, openPreview, switchPreviewVideo, type Preview } from '@/lib/meetings/rtk'
import { trackDeviceId } from '@/lib/meetings/devices'
import { useAudioLevel } from '@/lib/meetings/audio-level'

/**
 * The meeting room (PLAN-085 §1.5).
 *
 * Deliberately shaped around the *document*: video is a small floating dock,
 * the controls are a bar at the bottom, and the middle of the page belongs to
 * the meeting notes. RealtimeKit's core SDK does the media; the UI is ours.
 *
 * Since board fqw7 the route is a **view** over the project's meeting
 * session (`MeetingSessionProvider` in the project layout): the media
 * client, the room socket and the transcriber live there and survive a
 * navigation to a chat or a document. This page joins (pre-join → session),
 * shows the stage while the session is live for its code, and shows the
 * ended screen after.
 */
export const meetRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/meet/$code',
  component: MeetPage,
})

const BlockEditor = lazy(() =>
  import('@/components/documents/block-editor').then((mod) => ({ default: mod.BlockEditor }))
)
const HubBubble = lazy(() =>
  import('@/components/chat/hub-bubble').then((mod) => ({ default: mod.HubBubble }))
)

type JoinResult = Awaited<ReturnType<typeof orpc.meetings.join>>

function MeetPage() {
  const { projectId } = meetRoute.useParams()
  return (
    <MeetFeatureGate projectId={projectId}>
      <MeetingRoute />
    </MeetFeatureGate>
  )
}

type Phase = 'loading' | 'prejoin' | 'waiting' | 'ended' | 'error'

/** Never re-poll the waiting screen faster than this. */
const WAITING_POLL_FLOOR_MS = 5_000

function MeetingRoute() {
  const { projectId, code } = meetRoute.useParams()
  const { t } = useTranslation('meet')
  const navigate = useNavigate()
  const session = useMeetingSession()

  // The session's view of this meeting: live here, coming back after a
  // reload, or just ended. Any of these means the join flow below is not
  // needed — and must not run, or it would mint a second token. 'joining'
  // is not "live here": the pre-join screen stays up until media is
  // published, exactly as before.
  const mine = session.state.session?.code === code ? session.state : null
  const liveHere = mine !== null && (mine.status === 'active' || mine.status === 'reconnecting')
  const endedHere = session.state.ended?.code === code ? session.state.ended : null

  const [phase, setPhase] = useState<Phase>('loading')
  const [joined, setJoined] = useState<JoinResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * `quiet` keeps whatever is on screen while the request runs. The waiting
   * screen re-polls, and flipping to the spinner each time made it flash
   * once a second whenever the browser's clock ran ahead of the server's
   * (REVIEW-2 #4).
   */
  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setPhase('loading')
      setError(null)
      try {
        const result = await orpc.meetings.join({ projectId, code })
        setJoined(result)
        if (result.meeting.roomStatus === 'ended') {
          setPhase('ended')
        } else if (result.waitingUntil) {
          // The room is not open yet: the server minted no token and started
          // nothing, so the link is a countdown until it does (REVIEW-1 #1).
          setPhase('waiting')
        } else {
          setPhase('prejoin')
        }
      } catch (err) {
        const data = (err as { data?: { reason?: string } })?.data
        const status = (err as { status?: number })?.status
        setError(
          data?.reason === 'CREDITS_EXHAUSTED'
            ? t('errors.outOfCredits')
            : data?.reason === 'NOT_CONFIGURED'
              ? t('notConfigured.description')
              : status === 404
                ? t('notFound.description')
                : t('errors.joinFailed')
        )
        setPhase('error')
      }
    },
    [projectId, code, t]
  )

  // While the provider is still deciding whether a stored session can be
  // resumed, nothing is loaded: a reload must not mint a token in parallel.
  const reconnecting = session.state.status === 'reconnecting'
  useEffect(() => {
    if (liveHere || endedHere || reconnecting) return
    void load()
  }, [load, liveHere, endedHere, reconnecting])

  // Re-ask the server when the window opens, so a tab left on the waiting
  // screen moves into the meeting on its own. The 5 s floor matters when the
  // browser's clock runs ahead of the server's: the server keeps answering
  // `waitingUntil` in the client's past, and a 1 s floor turned that into a
  // request per second (REVIEW-2 #4).
  const waitingUntil = joined?.waitingUntil ?? null
  const [waitTick, setWaitTick] = useState(0)
  useEffect(() => {
    if (phase !== 'waiting' || !waitingUntil) return
    const delay = Date.parse(waitingUntil) - Date.now()
    if (Number.isNaN(delay)) return
    const id = setTimeout(
      () => void load(true).then(() => setWaitTick((tick) => tick + 1)),
      Math.max(delay, WAITING_POLL_FLOOR_MS)
    )
    return () => clearTimeout(id)
    // `waitTick` re-arms the timer: a skewed clock keeps answering with the
    // same `waitingUntil`, so nothing else in the deps would change and the
    // screen would poll once and then wait forever.
  }, [phase, waitingUntil, waitTick, load])

  if (liveHere && mine?.session) {
    if (mine.status === 'reconnecting') {
      return <FullSpinner label={t('reconnecting')} />
    }
    return <MeetingStage key={mine.session.meeting.id} projectId={projectId} code={code} />
  }

  if (endedHere) {
    return (
      <EndedScreen
        projectId={projectId}
        reason={endedHere.reason}
        notesDocumentId={endedHere.notesDocumentId}
      />
    )
  }

  if (phase === 'loading' || reconnecting) {
    return <FullSpinner label={t('loading')} />
  }

  if (phase === 'error' || !joined) {
    return (
      <div className="flex h-full flex-1 items-center justify-center p-10">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <VideoOff className="text-muted-foreground/60 h-8 w-8" />
          <h1 className="text-lg font-semibold">{t('notFound.title')}</h1>
          <p className="text-muted-foreground text-sm">{error}</p>
          <div className="mt-2 flex gap-2">
            <Button variant="outline" onClick={() => void load()}>
              {t('errors.retry')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/$projectId/calendar', params: { projectId } })}
            >
              {t('notFound.back')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (phase === 'ended') {
    return (
      <EndedScreen projectId={projectId} reason={null} notesDocumentId={joined.notesDocumentId} />
    )
  }

  if (phase === 'waiting') {
    return (
      <WaitingScreen
        projectId={projectId}
        title={joined.meeting.title}
        startAt={joined.meeting.startAt}
        opensAt={joined.waitingUntil}
        notesDocumentId={joined.notesDocumentId}
        onJoin={() => void load(true)}
      />
    )
  }

  return <PreJoinFlow key={joined.meeting.id} projectId={projectId} code={code} joined={joined} />
}

/**
 * The pre-join screen: our own camera preview, the SDK client initialised
 * in the session meanwhile, and Join hands both over to the session.
 */
function PreJoinFlow({
  projectId,
  code,
  joined,
}: {
  projectId: string
  code: string
  joined: JoinResult
}) {
  const navigate = useNavigate()
  const session = useMeetingSession()
  const [preview, setPreview] = useState<Preview | null>(null)
  const [permissionDenied, setPermissionDenied] = useState({ audio: false, video: false })
  const [busy, setBusy] = useState(false)
  const [prepared, setPrepared] = useState(() => session.isPrepared(code))
  // React 19 StrictMode runs effects twice; without this guard the preview
  // is opened twice and one camera stream is leaked (S2).
  const initialised = useRef(false)
  const previewRef = useRef<Preview | null>(null)
  previewRef.current = preview
  const mounted = useRef(false)

  const input: JoinInput = {
    projectId,
    code,
    meeting: { id: joined.meeting.id, title: joined.meeting.title },
    self: joined.self,
    notesDocumentId: joined.notesDocumentId,
    rtk: joined.rtk,
  }

  useEffect(() => {
    if (initialised.current) return
    initialised.current = true
    void (async () => {
      // The preview is our own getUserMedia stream, deliberately separate
      // from the SDK — see clientOptions().
      const opened = await openPreview()
      setPreview(opened)
      setPermissionDenied(opened.denied)
      try {
        await session.prepare(input)
        setPrepared(true)
      } catch (err) {
        console.error('[meet] could not initialise RealtimeKit', err)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per meeting
  }, [])

  // Left the pre-join without joining (a sidebar link, the back button):
  // hand the camera back and drop the prepared client — deferred, because
  // StrictMode's simulated unmount is followed by a remount at once.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      setTimeout(() => {
        if (mounted.current) return
        closePreview(previewRef.current)
        session.cancelPrepare()
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the session is stable
  }, [])

  const join = useCallback(
    async (choice: PreJoinChoice) => {
      setBusy(true)
      // Hand the camera and microphone back before RealtimeKit opens them:
      // the LED stays on otherwise, and some devices refuse a second capture
      // while the first is live (REVIEW-1 #8).
      closePreview(preview)
      setPreview(null)
      try {
        await session.join(input, choice)
      } catch (err) {
        console.error('[meet] join failed', err)
        setBusy(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `input` is rebuilt from `joined` each render
    [session, preview, joined]
  )

  return (
    <PreJoin
      title={joined.meeting.title}
      previewTrack={preview?.videoTrack ?? null}
      previewAudioTrack={preview?.stream.getAudioTracks()[0] ?? null}
      audioDevices={preview?.devices.audioInputs ?? []}
      videoDevices={preview?.devices.videoInputs ?? []}
      permissionDenied={permissionDenied}
      // Without RealtimeKit (the dev path, MEETINGS_ALLOW_NO_RTK) there is
      // no client to wait for.
      joining={busy || (Boolean(joined.rtk) && !prepared)}
      onChange={(choice) => {
        // Show the camera that will be published, not the first one.
        if (
          preview &&
          choice.videoDeviceId &&
          choice.videoDeviceId !== trackDeviceId(preview.videoTrack)
        ) {
          void switchPreviewVideo(preview, choice.videoDeviceId).then(setPreview)
        }
      }}
      onJoin={(choice) => void join(choice)}
      onCancel={() => {
        closePreview(preview)
        session.cancelPrepare()
        navigate({ to: '/$projectId/calendar', params: { projectId } })
      }}
    />
  )
}

/** The in-meeting layout: document in the middle, dock and bar floating. */
function MeetingStage({ projectId, code }: { projectId: string; code: string }) {
  const { t } = useTranslation('meet')
  const { me } = useAuthState()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const session = useMeetingSession()
  const { media, room, roomActions } = session
  // The stage only renders while the session is live for this code.
  const stored = session.state.session!
  const editorRef = useRef<BlockEditorHandle>(null)
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [endOpen, setEndOpen] = useState(false)
  const [micDenied, setMicDenied] = useState(false)
  // The live panel starts hidden (board m5gc #17); opening it is remembered
  // for the browser session only, so every new session starts on the document.
  const [panelOpen, setPanelOpen] = useState(() => {
    try {
      return sessionStorage.getItem('hub:meet:panel') === 'open'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem('hub:meet:panel', panelOpen ? 'open' : 'closed')
    } catch {
      // Private mode: the choice simply is not remembered.
    }
  }, [panelOpen])

  const documentId = stored.notesDocumentId
  const doc = useDocumentClient(projectId, documentId ?? '', Boolean(documentId))

  const leave = useCallback(() => {
    setBusy(true)
    // `leave` flips the session to 'leaving' before its first await, so the
    // calendar never sees an "ongoing meeting" card for a meeting being left.
    void session.leave()
    navigate({ to: '/$projectId/calendar', params: { projectId } })
  }, [session, navigate, projectId])

  const endMeeting = useCallback(async () => {
    setBusy(true)
    try {
      await orpc.meetings.end({ projectId, code })
      // The room's `ended` broadcast settles the session too; doing it here
      // as well means the ended screen does not wait for the socket.
      session.markEnded('host')
    } catch (err) {
      // The dialog stays open with its buttons re-enabled: the host can try
      // again or cancel, instead of a failed end vanishing silently.
      console.error('[meet] could not end the meeting', err)
    } finally {
      setBusy(false)
    }
  }, [projectId, code, session])

  // The local mic level (board m5gc #20/#21): a CSS variable on the stage
  // for the mic button's meter — set per frame, no React state — and a
  // speaking flag (hysteresis) for your own tile.
  const stageRef = useRef<HTMLDivElement>(null)
  const [selfSpeaking, setSelfSpeaking] = useState(false)
  useAudioLevel(media.audioTrack, media.micOn, {
    onLevel: (level) => {
      // Speech peaks around 0.3 RMS; map that to a full button.
      stageRef.current?.style.setProperty('--mic-level', Math.min(1, level / 0.3).toFixed(3))
    },
    onSpeaking: setSelfSpeaking,
  })

  // Everyone the room knows about, whether or not their camera is up: a
  // participant on a dead tab still counts.
  const participantCount = Math.max(
    room.participants.filter((p) => p.connected).length,
    media.remotes.length + (media.self ? 1 : 0)
  )
  // Who has media flowing — RealtimeKit carries the HUB user id on each participant.
  const mediaUserIds = new Set(
    [...media.remotes.map((p) => p.userId), media.self ? stored.self.userId : null].filter(
      (id): id is string => Boolean(id)
    )
  )

  // Everyone sees the pause; only the host can lift it. A host pause is
  // the room's state, so a reload or a second tab shows it too.
  const pausedBy = room.transcription === 'paused' ? room.pausedBy : null
  const hostPaused = pausedBy === 'host'
  // Raised hands (board m5gc #12) live in the room's participant list.
  const raisedHands = new Set(room.participants.filter((p) => p.handRaisedAt).map((p) => p.userId))
  const ownHandRaised = raisedHands.has(stored.self.userId)
  // Avatars for the tiles come from the room (the same list the header uses).
  const avatars = new Map(room.participants.map((p) => [p.userId, p.avatarUrl]))

  return (
    <div ref={stageRef} className="relative flex h-full flex-1 flex-col">
      {stored.self.isHost && (
        <RaisedHandsList
          participants={room.participants}
          selfUserId={stored.self.userId}
          rightOffset={panelOpen && !isMobile ? 332 : 12}
          onLower={(userId) => roomActions.lowerHand(userId)}
        />
      )}
      {pausedBy && (
        <TranscriptionBanner
          pausedBy={pausedBy}
          canResume={stored.self.isHost}
          onResume={() => roomActions.setTranscriptionPaused(false)}
        />
      )}
      <div className="relative flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          {documentId && doc.client && doc.ready ? (
            <div className="flex-1 overflow-y-auto pb-24">
              <div className="mx-auto max-w-3xl px-4 py-8">
                <Suspense fallback={<StageSpinner label={t('loading')} />}>
                  <BlockEditor
                    ref={editorRef}
                    client={doc.client}
                    projectId={projectId}
                    initialBlocks={doc.initialBlocks}
                    presence={doc.presence}
                    currentUserId={me?.user.id}
                    onSelectionChange={setSelectedBlockIds}
                  />
                </Suspense>
              </div>
            </div>
          ) : (
            <StageSpinner label={t('loading')} />
          )}
        </div>

        {panelOpen && (
          <LivePanel
            segments={room.segments}
            summary={room.summary}
            startedAt={room.startedAt}
            onClose={() => setPanelOpen(false)}
          />
        )}

        {media.sharing && media.self && (
          <SharePreview
            track={media.self.screenShareTrack}
            onStop={() => void media.toggleShare()}
          />
        )}

        <VideoDock
          self={media.self}
          remotes={media.remotes}
          activeSpeakerId={media.activeSpeakerId}
          raisedHands={raisedHands}
          avatars={avatars}
          selfSpeaking={selfSpeaking}
          anchor={{ right: panelOpen && !isMobile ? 336 : 16, bottom: 88 }}
        />

        <ControlBar
          micOn={media.micOn}
          cameraOn={media.cameraOn}
          sharing={media.sharing}
          audioDevices={media.audioDevices}
          videoDevices={media.videoDevices}
          audioDeviceId={media.audioDeviceId}
          videoDeviceId={media.videoDeviceId}
          onSelectAudioDevice={(id) => void media.setAudioDevice(id)}
          onSelectVideoDevice={(id) => void media.setVideoDevice(id)}
          panelOpen={panelOpen}
          handRaised={ownHandRaised}
          participants={participantCount}
          participantList={room.participants}
          mediaUserIds={mediaUserIds}
          selfUserId={stored.self.userId}
          transcription={room.transcription}
          pausedBy={pausedBy}
          canPauseTranscription={stored.self.isHost}
          canEnd={stored.self.canEnd}
          busy={busy}
          onToggleTranscription={() => roomActions.setTranscriptionPaused(!hostPaused)}
          onToggleMic={() =>
            void media.toggleMic().then((ok) => {
              if (!ok) setMicDenied(true)
            })
          }
          onToggleCamera={() => void media.toggleCamera()}
          onToggleShare={() => void media.toggleShare()}
          onTogglePanel={() => setPanelOpen((value) => !value)}
          onToggleHand={() => roomActions.setHandRaised(!ownHandRaised)}
          onLeave={leave}
          onEnd={() => setEndOpen(true)}
        />

        {documentId && doc.client && (
          <Suspense fallback={null}>
            <HubBubble
              projectId={projectId}
              view="meet"
              avoidRight={panelOpen && !isMobile ? 320 : 0}
              context={{
                kind: 'document',
                documentId,
                documentTitle: stored.meeting.title,
                selectedBlockIds,
                blocks: doc.client.blocks,
                onClearSelection: () => setSelectedBlockIds([]),
                onSelectBlocks: setSelectedBlockIds,
                onJumpToBlock: (id) => editorRef.current?.scrollToBlock(id),
              }}
            />
          </Suspense>
        )}

        {micDenied && (
          <p className="pointer-events-none absolute top-4 right-4 max-w-xs text-right text-xs text-amber-600 dark:text-amber-400">
            {t('errors.microphoneBlocked')}
          </p>
        )}
        {!stored.rtk && !micDenied && (
          <p className="text-muted-foreground pointer-events-none absolute top-4 right-4 text-xs">
            {t('notConfigured.title')}
          </p>
        )}
      </div>

      <EndMeetingDialog
        open={endOpen}
        busy={busy}
        onOpenChange={setEndOpen}
        onConfirm={() => void endMeeting()}
      />
    </div>
  )
}

function FullSpinner({ label }: { label: string }) {
  return (
    <div className="flex h-full flex-1 items-center justify-center gap-2">
      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
      <span className="text-muted-foreground text-sm">{label}</span>
    </div>
  )
}

function StageSpinner({ label }: { label: string }) {
  return (
    <div className="flex flex-1 items-center justify-center gap-2">
      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
      <span className="text-muted-foreground text-sm">{label}</span>
    </div>
  )
}
